/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import AudioCollection from '../Audio/AudioCollection';
import AudioManager from '../Audio/AudioManager';
import AudioTrigger from '../Audio/AudioTrigger';
import Currency from '../Localization/Currency';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import CLog from '../Utility/ComponentLogger';
import BetManager from './BetManager';
import MusicalBangupInfo from './MusicalBangupInfo';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Bangup duration and audio determined by a lookup table of win-to-bet ratios
 */
@cc._decorator.ccclass
@cc._decorator.executeInEditMode
export default class MusicalBangup extends StateDependencyComponent
{
    @property({ type: AudioCollection })
    protected get setFromAudioCollection(): AudioCollection { return null; }
    protected set setFromAudioCollection(value: AudioCollection)
    {
        if (!value) { return; }

        this.bangupInfos.length = 0;

        for (let childNode of value.node.children)
        {
            let audioTrigger: AudioTrigger = childNode.getComponent(AudioTrigger);

            if (!audioTrigger) { continue; }

            let bangupInfo: MusicalBangupInfo = new MusicalBangupInfo();

            bangupInfo.AudioTrigger = audioTrigger;
            bangupInfo.Duration = audioTrigger.Duration;
            bangupInfo.Ratio = audioTrigger.Duration * this.RatioMultiplier;

            this.bangupInfos.push(bangupInfo);
        }
    }

    @property(cc.Float)
    protected RatioMultiplier: number = 2;

    /** Minmum ratio needed to play bangup complete sound. */
    @property(
        {
            type: cc.Float,
            tooltip: 'Minmum ratio needed to play bangup complete sound.'
        })
    protected minRatioBangupComplete: number = -1;
    public get MinRatioBangupComplete(): number { return this.minRatioBangupComplete; }

    @property([MusicalBangupInfo])
    protected bangupInfos: MusicalBangupInfo[] = [new MusicalBangupInfo()];

    private _lastBangupInfo: MusicalBangupInfo = null;
    public get LastBangupInfo(): MusicalBangupInfo { return this._lastBangupInfo; }

    private _audioManagerRef: InjectableComponent<AudioManager> = new InjectableComponent<AudioManager>();

    private _betManagerRef: InjectableComponent<BetManager> = new InjectableComponent<BetManager>();

    private _curAudioTrigger: string = '';

    /** @hidden */
    protected setContexts(): void
    {
        if (CC_EDITOR) { return; }

        this._audioManagerRef.SetContext(AudioManager);
        this._betManagerRef.SetContext(BetManager);
    }

    /**
     * Play the appropriate sound for the given bangup amount
     * @param bangupAmount Total value to bang up
     * @returns bangupRate
     */
    public PlayBangupAudio(bangupAmount: Currency): Currency
    {
        let bangupInfo: MusicalBangupInfo = this.getBangupInfo(bangupAmount);

        this._curAudioTrigger = bangupInfo.AudioTrigger.AudioTriggerID;

        this._audioManagerRef.Value.TriggerSound(null, this._curAudioTrigger);
        this._lastBangupInfo = bangupInfo;

        return this.getBangupRate(bangupAmount);
    }

    /** Stop currently playing audio. */
    public StopBangupAudio(delay: number = 0): void
    {
        this.scheduleOnce(() => this._audioManagerRef.Value.StopTrigger(null, this._curAudioTrigger), delay);
    }

    /**
     * Get the speed of bangup based on given bangup amount and look-up of win-to-bet ratio
     * @param bangupAmount Total value to bang up
     */
    private getBangupRate(bangupAmount: Currency): Currency
    {
        if (!this.validateProperties()) { return; }

        let bangupInfo: MusicalBangupInfo = this.getBangupInfo(bangupAmount);
        let bangupRate: number = bangupAmount.GetValue() / bangupInfo.Duration;

        return new Currency(bangupAmount, bangupRate);
    }

    /**
     * This helper is mainly due to the difficulty of having dictionaries/hashtables exposed to the editor
     * Gets the array index for a given bangup amount so that it can be used to access the corresponding
     * elements in the audio and duration lists
     * @param bangupAmount
     */
    private getBangupInfo(bangupAmount: Currency): MusicalBangupInfo
    {
        // win-to-bet ratio
        let ratio: number = bangupAmount.GetValue() / this._betManagerRef.Value.CurrentBet.GetValue();

        return this.bangupInfos.find(bangupInfo => bangupInfo.Ratio >= ratio)
            || this.bangupInfos[this.bangupInfos.length - 1];
    }

    /**
     * Verify preconditions expected for proper execution or else log errors and fail
     */
    private validateProperties(): boolean
    {
        if (this.bangupInfos.length === 0)
        {
            CLog.error("No ratios defined");
            return false;
        }

        return true;
    }
}
